import type { Timeseries } from "../types";
import type { TimeGrain } from "../config/plots";
import type { ContinuousData, ChartData, DimensionSeries } from "../config/data";

export function getBucketStart(date: Date, grain: TimeGrain): number {
  if (grain === "day") {
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  }
  const dayStart = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayOfWeek = dayStart.getUTCDay();
  const offset = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // align to Monday start
  dayStart.setUTCDate(dayStart.getUTCDate() - offset);
  return dayStart.getTime();
}

export function filterBuckets<T extends { daysWithData: number }>(
  buckets: Map<number, T>,
  grain: TimeGrain,
): Array<[number, T]> {
  return Array.from(buckets.entries()).filter(([, bucket]) => grain !== "week" || bucket.daysWithData === 7);
}

export function resampleTimeseries(ts: Timeseries, grain: TimeGrain, strategy: "sum" | "average"): Timeseries {
  if (grain === "day") return ts;
  const buckets = new Map<number, { sum: number; count: number; daysWithData: number; hasNull: boolean }>();

  for (let i = 0; i < ts.timestamps.length; i++) {
    const timestamp = ts.timestamps[i];
    const value = ts.data[i];
    const bucketKey = getBucketStart(timestamp, grain);
    const bucket = buckets.get(bucketKey) ?? { sum: 0, count: 0, daysWithData: 0, hasNull: false };
    if (value != null) {
      bucket.sum += Number(value);
      bucket.count += 1;
    } else {
      bucket.hasNull = true;
    }
    bucket.daysWithData += 1;
    buckets.set(bucketKey, bucket);
  }

  const sorted = filterBuckets(buckets, grain);

  return {
    timestamps: sorted.map(([ms]) => new Date(ms)),
    data: sorted.map(([, bucket]) =>
      bucket.hasNull ? null : strategy === "sum" ? bucket.sum : bucket.sum / bucket.count,
    ),
  } satisfies Timeseries;
}

function isTimeseries(data: ChartData): data is Timeseries {
  return Array.isArray((data as Timeseries).timestamps);
}

function isContinuousData(data: ChartData): data is ContinuousData {
  return "mean" in data && "std" in data && "normalizeBy" in data;
}

function isDimensionSeries(data: ChartData): data is DimensionSeries {
  return !isTimeseries(data) && !isContinuousData(data);
}

export function resampleContinuousData(data: ChartData, grain: TimeGrain): ChartData {
  if (isContinuousData(data)) {
    if (grain === "day") return data;

    type Bucket = {
      totalN: number;
      weightedMeanSum: number;
      sumNiMuSq: number;
      sumNiSi2: number;
      daysWithData: number;
    };

    const buckets = new Map<number, Bucket>();

    const len = data.normalizeBy.timestamps.length;
    for (let i = 0; i < len; i++) {
      const timestamp = data.normalizeBy.timestamps[i];
      if (!timestamp) continue;
      if (data.mean.data[i] == null || data.std.data[i] == null || data.normalizeBy.data[i] == null) continue;

      const n = Number(data.normalizeBy.data[i]);
      const mu = Number(data.mean.data[i]);
      const s = Number(data.std.data[i]);
      if (!isFinite(n) || n <= 0) continue;

      const bucketKey = getBucketStart(timestamp, grain);
      const bucket = buckets.get(bucketKey) ?? {
        totalN: 0,
        weightedMeanSum: 0,
        sumNiMuSq: 0,
        sumNiSi2: 0,
        daysWithData: 0,
      };

      bucket.totalN += n;
      bucket.weightedMeanSum += mu * n;
      bucket.sumNiMuSq += n * mu * mu;
      const s2 = isFinite(s) ? s * s : 0;
      bucket.sumNiSi2 += n * s2;
      bucket.daysWithData += 1;

      buckets.set(bucketKey, bucket);
    }

    const sorted = filterBuckets(buckets, grain);
    const meanTs: Timeseries = {
      timestamps: sorted.map(([ms]) => new Date(ms)),
      data: sorted.map(([, b]) => (b.totalN > 0 ? b.weightedMeanSum / b.totalN : null)),
    };
    const stdTs: Timeseries = {
      timestamps: meanTs.timestamps,
      data: sorted.map(([, b]) => {
        if (b.totalN <= 0) return null;
        const mu = b.weightedMeanSum / b.totalN;
        const sumNiMuDiffSq = b.sumNiMuSq - 2 * mu * b.weightedMeanSum + mu * mu * b.totalN;
        const variance = (b.sumNiSi2 + sumNiMuDiffSq) / b.totalN;
        return Math.sqrt(Math.max(variance < 0 && Math.abs(variance) < 1e-12 ? 0 : variance, 0));
      }),
    };
    const nTs: Timeseries = {
      timestamps: meanTs.timestamps,
      data: sorted.map(([, b]) => b.totalN),
    };
    return { mean: meanTs, std: stdTs, normalizeBy: nTs } as ContinuousData;
  }

  if (isDimensionSeries(data)) {
    const result: DimensionSeries = {};
    for (const [key, series] of Object.entries(data)) {
      result[key] = resampleTimeseries(series, grain, "average");
    }
    return result;
  }

  return data;
}
